import React, { useEffect } from 'react'
import { useConfig } from '@peracto/peracto-config'
import TextInput from '@peracto/peracto-editor-ui/dist/TextInput'
import File from '@peracto/peracto-editor-ui/dist/File'
import Select from '@peracto/peracto-editor-ui/dist/Select'
import Checkbox from '@peracto/peracto-editor-ui/dist/Checkbox'
import Slider from '@peracto/peracto-editor-ui/dist/Slider'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'

/**
 * Enhanced Buttons Form - Editor Controls
 *
 * Restores all features from Chakra version:
 * - Button size option (xs, sm, md, lg)
 * - Ghost style option
 * - All original controls
 */
const ButtonsForm = ({
    onChange,
    state: {
        buttons,
        direction = 'row',
        justifyContent = 'flex-start',
        spacing = 10,
        selectedButtonIndex,
    },
}) => {
    const config = useConfig()
    const { buttonColorOptions = [] } = config.get('editorConfig') || {}

    // Use colorOptions from editorConfig (defined in App.js)
    const buttonColours = buttonColorOptions

    // Create default button
    const DEFAULT_BUTTON = {
        label: 'Click Me',
        url: '#',
        openInNewTab: false,
        size: 'md',
        variantStyle: 'solid',
        variant: 'primary',
        width: 'auto',
    }

    const JUSTIFICATION_OPTIONS = [
        {
            label: 'Start',
            value: 'flex-start',
        },
        {
            label: 'Centre',
            value: 'center',
        },
        {
            label: 'End',
            value: 'flex-end',
        },
    ]

    if (direction === 'row') {
        JUSTIFICATION_OPTIONS.push(
            {
                label: 'Space Between',
                value: 'space-between',
            },
            {
                label: 'Space Around',
                value: 'space-around',
            }
        )
    }

    useEffect(() => {
        return () => {
            onChange({ selectedButtonIndex: null })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <>
            <Select
                label="Buttons Direction"
                options={[
                    {
                        label: 'Row',
                        value: 'row',
                    },
                    {
                        label: 'Column',
                        value: 'column',
                    },
                ]}
                value={direction}
                onChange={(e) => onChange({ direction: e.value })}
            />

            <Select
                label="Justify Buttons"
                options={JUSTIFICATION_OPTIONS}
                value={justifyContent}
                onChange={(e) => onChange({ justifyContent: e.value })}
            />

            <Slider
                label="Button Spacing"
                min={0}
                max={30}
                step={1}
                value={spacing}
                onChange={(e) => onChange({ spacing: e.target.value })}
            />

            <hr />

            {selectedButtonIndex !== null && buttons && buttons[selectedButtonIndex] && (
                <>
                    <TextInput
                        label="Button Label"
                        onChange={(e) => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].label = e.target.value

                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                        value={buttons[selectedButtonIndex].label}
                    />

                    <File
                        label="Button Link"
                        onChange={(value) => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].url = value
                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                        value={buttons[selectedButtonIndex].url}
                    />

                    <Checkbox
                        label="Open in new tab"
                        checked={buttons[selectedButtonIndex].openInNewTab}
                        onChange={(e) => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].openInNewTab = e.target.checked
                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                    />

                    <Checkbox
                        label="Lowercase text"
                        checked={buttons[selectedButtonIndex].lowercaseText}
                        onChange={(e) => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].lowercaseText = e.target.checked
                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                        tooltip="Buttons are uppercase by default. Enable this to keep the label's original casing instead."
                    />

                    <Select
                        label="Button Colour"
                        options={buttonColours}
                        value={buttons[selectedButtonIndex].variant || 'primary'}
                        onChange={(e) => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].variant = e.value
                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                    />

                    {/* Button Size - ALWAYS show this (removed isTailwind check) */}
                    <Select
                        label="Button Size"
                        options={[
                            {
                                label: 'Extra Small',
                                value: 'xs',
                            },
                            {
                                label: 'Small',
                                value: 'sm',
                            },
                            {
                                label: 'Medium',
                                value: 'md',
                            },
                            {
                                label: 'Large',
                                value: 'lg',
                            },
                        ]}
                        value={buttons[selectedButtonIndex].size || 'md'}
                        onChange={(e) => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].size = e.value
                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                    />

                    {/* Button Style - solid, outline, ghost, link */}
                    <Select
                        label="Button Style"
                        options={[
                            {
                                label: 'Solid',
                                value: 'solid',
                            },
                            {
                                label: 'Outline',
                                value: 'outline',
                            },
                            {
                                label: 'Ghost',
                                value: 'ghost',
                            },
                            {
                                label: 'Link',
                                value: 'link',
                            },
                        ]}
                        value={buttons[selectedButtonIndex].variantStyle || 'solid'}
                        onChange={(e) => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].variantStyle = e.value
                            buttonsClone[selectedButtonIndex].style = null // Clear old field
                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                    />

                    <Select
                        label="Button Width"
                        options={[
                            {
                                label: 'Auto',
                                value: 'auto',
                            },
                            {
                                label: 'Full',
                                value: 'full',
                            },
                        ]}
                        value={buttons[selectedButtonIndex].width || 'auto'}
                        onChange={(e) => {
                            const buttonsClone = [...buttons]
                            buttonsClone[selectedButtonIndex].width = e.value
                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                    />

                    <button
                        className="btn btn-sm btn-danger w-100 mt-2"
                        onClick={(e) => {
                            const buttonsClone = [...buttons]

                            buttonsClone.splice(selectedButtonIndex, 1)

                            onChange({
                                buttons: buttonsClone,
                            })
                        }}
                    >
                        <FontAwesomeIcon icon={faTrash} className="mr-2" /> Remove Button
                    </button>
                </>
            )}

            <hr className="my-2" />

            <button
                className="btn btn-sm btn-secondary w-100"
                onClick={(e) => {
                    const buttonsClone = [...buttons, DEFAULT_BUTTON]
                    onChange({
                        buttons: buttonsClone,
                    })
                }}
            >
                <FontAwesomeIcon icon={faPlusCircle} className="mr-2" /> Add Button
            </button>
        </>
    )
}

export default ButtonsForm
